import { useEffect, useMemo, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import {
  ArrowDown,
  ArrowUp,
  ArrowsDownUp,
  Check,
  LockSimple,
  Warning,
  X,
} from '@phosphor-icons/react'
import { apiRequest, errorMessage, jsonBody } from '../api/client'
import type {
  ReorderWorkoutItemsResult,
  Workout,
  WorkoutItem,
} from '../api/types'
import { queryClient } from '../app/queryClient'

type WorkoutOrderGroup = {
  key: string
  itemIds: string[]
  items: WorkoutItem[]
  label: string
  locked: boolean
  sessionPairCode: string | null
}

function buildOrderGroups(items: WorkoutItem[]): WorkoutOrderGroup[] {
  const groups: WorkoutOrderGroup[] = []
  for (let index = 0; index < items.length;) {
    const first = items[index]
    const groupedItems = [first]
    if (first.sessionPairCode) {
      let next = index + 1
      while (
        next < items.length &&
        items[next].sessionPairCode === first.sessionPairCode
      ) {
        groupedItems.push(items[next])
        next += 1
      }
    }

    groups.push({
      key: first.sessionPairCode
        ? `pair-${first.sessionPairCode}`
        : `item-${first.id}`,
      itemIds: groupedItems.map((item) => item.id),
      items: groupedItems,
      label: groupedItems.map((item) => item.exerciseName).join(' + '),
      locked: groupedItems.some((item) => item.sets.length > 0),
      sessionPairCode: first.sessionPairCode,
    })
    index += groupedItems.length
  }
  return groups
}

export function WorkoutOrderEditor({ workout, offline = false }: { workout: Workout; offline?: boolean }) {
  const [open, setOpen] = useState(false)
  const [orderedIds, setOrderedIds] = useState(
    workout.items.map((item) => item.id),
  )
  const [preview, setPreview] = useState<ReorderWorkoutItemsResult | null>(null)
  const [savedMessage, setSavedMessage] = useState<string | null>(null)

  useEffect(() => {
    if (!open) {
      setOrderedIds(workout.items.map((item) => item.id))
    }
  }, [open, workout.items, workout.stateVersion])

  const itemById = useMemo(
    () => new Map(workout.items.map((item) => [item.id, item])),
    [workout.items],
  )
  const orderedItems = orderedIds
    .map((id) => itemById.get(id))
    .filter((item): item is WorkoutItem => Boolean(item))
  const groups = buildOrderGroups(orderedItems)
  const originalIds = workout.items.map((item) => item.id)
  const changed = orderedIds.join('|') !== originalIds.join('|')

  const refresh = async () => {
    await queryClient.invalidateQueries({
      queryKey: ['workout', workout.localDate],
    })
    await queryClient.invalidateQueries({
      queryKey: ['coach-decisions', workout.id],
    })
    await queryClient.invalidateQueries({
      queryKey: ['coach-activity', workout.id],
    })
  }

  const reorder = useMutation<ReorderWorkoutItemsResult, Error, boolean>({
    mutationFn: (confirmWarnings) =>
      apiRequest<ReorderWorkoutItemsResult>(
        `/workouts/${workout.id}/items/reorder`,
        {
          method: 'POST',
          body: jsonBody({
            orderedWorkoutItemIds: orderedIds,
            expectedWorkoutStateVersion: workout.stateVersion,
            confirmWarnings,
          }),
        },
      ),
    onSuccess: async (result) => {
      if (result.requiresConfirmation) {
        setPreview(result)
        return
      }

      setPreview(null)
      setOpen(false)
      setSavedMessage(
        result.applied
          ? 'Pořadí je uložené. Změnu lze 10 minut vrátit v záznamu coache u přesunutého cviku.'
          : 'Pořadí zůstalo beze změny.',
      )
      await refresh()
    },
  })

  const moveGroup = (index: number, direction: -1 | 1) => {
    const targetIndex = index + direction
    if (
      targetIndex < 0 ||
      targetIndex >= groups.length ||
      groups[index].locked ||
      groups[targetIndex].locked
    ) {
      return
    }

    const next = [...groups]
    ;[next[index], next[targetIndex]] = [next[targetIndex], next[index]]
    setOrderedIds(next.flatMap((group) => group.itemIds))
    setPreview(null)
    setSavedMessage(null)
  }

  const startEditing = () => {
    setOrderedIds(originalIds)
    setPreview(null)
    setSavedMessage(null)
    setOpen(true)
  }

  if (!open) {
    return (
      <section className="workout-order-launch" aria-label="Pořadí dnešních cviků">
        <div>
          <ArrowsDownUp size={21} />
          <span>
            <strong>Pořadí cviků</strong>
            <small>
              Přesun přepočítá kontext únavy; zapsané série zůstanou nedotčené.
            </small>
          </span>
        </div>
        <button
          type="button"
          className="button button--quiet"
          onClick={startEditing}
          disabled={workout.status === 'Completed' || offline}
          title={offline ? 'Přesun cviků vyžaduje připojení kvůli novému výpočtu kontextu.' : undefined}
        >
          <ArrowsDownUp size={18} />
          Upravit pořadí
        </button>
        {savedMessage && (
          <p className="workout-order-launch__saved" role="status">
            <Check size={17} weight="bold" />
            {savedMessage}
          </p>
        )}
      </section>
    )
  }

  return (
    <section
      className="workout-order-editor"
      aria-labelledby="workout-order-title"
    >
      <header>
        <div>
          <p className="eyebrow">Dnešní kontext</p>
          <h2 id="workout-order-title">Upravit pořadí cviků</h2>
          <p>
            A/B dvojice se přesouvá společně. Cvik s hotovou sérií je pevná
            kotva a nelze jej překročit.
          </p>
        </div>
        <button
          type="button"
          className="icon-button"
          aria-label="Zavřít úpravu pořadí"
          onClick={() => {
            setOpen(false)
            setPreview(null)
          }}
          disabled={reorder.isPending}
        >
          <X size={20} />
        </button>
      </header>

      <ol className="workout-order-list">
        {groups.map((group, index) => {
          const previous = groups[index - 1]
          const next = groups[index + 1]
          const canMoveUp =
            index > 0 && !group.locked && !previous?.locked
          const canMoveDown =
            index < groups.length - 1 && !group.locked && !next?.locked
          return (
            <li key={group.key}>
              <span className="workout-order-list__number">{index + 1}</span>
              <span className="workout-order-list__title">
                <strong>{group.label}</strong>
                <small>
                  {group.sessionPairCode
                    ? `A/B blok ${group.sessionPairCode} · přesouvá se společně`
                    : group.items[0].sessionRole === 'Protected'
                      ? 'Prioritní expozice'
                      : group.items[0].sessionRole === 'Optional'
                        ? 'Volitelná expozice'
                        : 'Flexibilní expozice'}
                </small>
              </span>
              {group.locked && (
                <span className="workout-order-list__lock">
                  <LockSimple size={16} weight="fill" />
                  Pevná pozice
                </span>
              )}
              <span className="workout-order-list__actions">
                <button
                  type="button"
                  className="icon-button"
                  aria-label={`Přesunout ${group.label} nahoru`}
                  title="Přesunout nahoru"
                  onClick={() => moveGroup(index, -1)}
                  disabled={!canMoveUp || reorder.isPending}
                >
                  <ArrowUp size={19} />
                </button>
                <button
                  type="button"
                  className="icon-button"
                  aria-label={`Přesunout ${group.label} dolů`}
                  title="Přesunout dolů"
                  onClick={() => moveGroup(index, 1)}
                  disabled={!canMoveDown || reorder.isPending}
                >
                  <ArrowDown size={19} />
                </button>
              </span>
            </li>
          )
        })}
      </ol>

      {preview?.requiresConfirmation && (
        <div
          className="workout-order-warning"
          role="alertdialog"
          aria-labelledby="workout-order-warning-title"
        >
          <Warning size={22} weight="fill" />
          <div>
            <strong id="workout-order-warning-title">
              Tato změna může ovlivnit výkon prioritního cviku
            </strong>
            {preview.warnings.map((warning) => {
              const protectedName =
                itemById.get(warning.protectedWorkoutItemId)?.exerciseName ??
                'Prioritní cvik'
              const crossedNames = warning.crossedWorkoutItemIds
                .map((id) => itemById.get(id)?.exerciseName)
                .filter(Boolean)
                .join(', ')
              return (
                <p key={`${warning.reasonCode}-${warning.protectedWorkoutItemId}`}>
                  {protectedName} se přesune za překrývající zátěž
                  {crossedNames ? ` (${crossedNames})` : ''}. Coach po potvrzení
                  přepočítá pořadí, únavu i interpretaci dalších sérií.
                </p>
              )
            })}
            <div className="button-row">
              <button
                type="button"
                className="button button--primary"
                onClick={() => reorder.mutate(true)}
                disabled={reorder.isPending}
              >
                {reorder.isPending ? 'Ukládám…' : 'Přesto změnit pořadí'}
              </button>
              <button
                type="button"
                className="button button--quiet"
                onClick={() => setPreview(null)}
                disabled={reorder.isPending}
              >
                Zachovat původní prioritu
              </button>
            </div>
          </div>
        </div>
      )}

      {!preview?.requiresConfirmation && (
        <div className="workout-order-editor__footer">
          <p>
            {changed
              ? 'Nové pořadí zatím není uložené.'
              : 'Pořadí je stejné jako při otevření.'}
          </p>
          <div className="button-row">
            <button
              type="button"
              className="button button--primary"
              onClick={() => reorder.mutate(false)}
              disabled={!changed || reorder.isPending}
            >
              {reorder.isPending ? 'Kontroluji…' : 'Zkontrolovat a uložit'}
            </button>
            <button
              type="button"
              className="button button--quiet"
              onClick={() => setOpen(false)}
              disabled={reorder.isPending}
            >
              Zrušit
            </button>
          </div>
        </div>
      )}

      {reorder.isError && (
        <p className="inline-error" role="alert">
          {errorMessage(reorder.error)}
        </p>
      )}
    </section>
  )
}
